import { Component, computed, effect, input, model, output, signal, untracked } from "@angular/core";
import {
  AbstractControl, FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from "@angular/forms";
import { RadioButtonModule } from "primeng/radiobutton";
import { SkeletonModule } from "primeng/skeleton";
import { CONSTANTS } from "../../../../../constants";
import { Area, Zone } from "../../../../../core/database/db";
import { ADD_NEW_ROOM_AREA } from "../../../../areas/models/AreaDTO";
import { FormFieldComponent } from "../../../../../shared/components/form-field/form-field.component";
import { HorizontalButton, HorizontalButtonGroupComponent } from "../../../../../shared/components/horizontal-button-group/horizontal-button-group.component";
import { SectionHeaderComponent } from "../../../../../shared/components/section-header/section-header.component";
import { StandardBottomSheetComponent } from "../../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { AreaConfigurationDTO } from "../../../../areas/models/configuration/AreaConfigurationDTO";

export interface AddAreaSubmit {
  /** `AreaConfigurationDTO.id` of the picked room type; stable however the list is sorted. */
  areaConfigId: number;
  name: string;
  /** Only set when the sheet collected the zone itself (BQ3/BQ4); BQ7 already knows the zone. */
  zoneId?: number;
}

/**
 * Add a Room bottom sheet.
 *
 * Used by BQ7's zone menu, where the zone is already known, and by the BQ3/BQ4 Needs Attention
 * sheet, where it slides in over the issue form and collects the zone (Interior/Exterior) first.
 * Rooms created here are unscoped, so every later build-quality step on the lot can use them.
 */
@Component({
  selector: "app-add-area-dialog",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RadioButtonModule,
    SkeletonModule,
    StandardBottomSheetComponent,
    SectionHeaderComponent,
    FormFieldComponent,
    HorizontalButtonGroupComponent
  ],
  templateUrl: "./add-area-dialog.component.html",
  styleUrl: "./add-area-dialog.component.scss",
})
export class AddAreaDialogComponent {
  visible = model<boolean>(false);
  /** Context line under the title; used where the zone is fixed (BQ7). */
  zoneName = input<string>("");
  areasConfig = input<AreaConfigurationDTO[] | undefined>(undefined);
  existingAreas = input<Area[]>([]);
  isSaving = input<boolean>(false);
  /** When supplied, the CM picks the zone here and it is required before a room can be added. */
  zoneOptions = input<Zone[]>([]);
  /** Slide in from the right instead of up from the bottom, for when this opens over another sheet. */
  slideIn = input<boolean>(false);

  readonly addArea = output<AddAreaSubmit>();

  protected readonly slideOpen = signal<boolean>(false);
  /** Mirrors the zone control so the button group restyles as soon as a zone is picked. */
  protected readonly selectedZoneId = signal<number | null>(null);

  /** Whole House is a checklist grouping, not somewhere a room can live. */
  private readonly selectableZones = computed(() =>
    this.zoneOptions()
      .filter(zone => zone.name.toLowerCase() !== CONSTANTS.ZONES.WHOLE_HOUSE)
      .sort((a, b) => a.name.localeCompare(b.name)));

  protected readonly showZoneSelector = computed(() => this.selectableZones().length > 0);

  /** Room types, alphabetical; `undefined` while the configuration is still loading. */
  protected readonly sortedAreasConfig = computed(() => {
    const config = this.areasConfig();
    return config ? [...config].sort((a, b) => a.name.localeCompare(b.name)) : undefined;
  });

  protected readonly zoneButtons = computed<HorizontalButton[]>(() => {
    const selectedZoneId = this.selectedZoneId();

    return this.selectableZones()
      .map(zone => ({
        label: zone.name,
        severity: "contrast" as const,
        outlined: zone.id !== selectedZoneId,
        styleClass: "bqa-primary-btn",
        value: zone.id,
        action: () => this.selectZone(zone.id),
        testId: `add-area-btn-zone-${zone.id}`
      }));
  });

  protected readonly footerButtons = computed<HorizontalButton[]>(() => [
    {
      label: "Cancel",
      severity: "contrast",
      outlined: true,
      disabled: this.isSaving(),
      styleClass: "bqa-primary-btn",
      action: () => this.onCancel(),
      testId: "job-areas-btn-add-room-cancel"
    },
    {
      label: this.isSaving() ? "Adding..." : "Add Room",
      severity: "contrast",
      disabled: this.isSaving(),
      loading: this.isSaving(),
      styleClass: "bqa-primary-btn",
      action: () => this.onAdd(),
      testId: "job-areas-btn-add-room-submit"
    }
  ]);

  protected form = new FormGroup({
    zoneId: new FormControl<number | null>(null),
    areaConfigId: new FormControl<number | null>(null, Validators.required),
    name: new FormControl("", {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern("^[0-9a-zA-Z' \\-/\\\\]{1,100}"),
        this.noWhiteSpaceValidator,
        this.reservedRoomNameValidator,
        this.uniqueRoomNamesValidator.bind(this)
      ],
    }),
  });

  constructor() {
    effect(() => {
      const isVisible = this.visible();
      untracked(() => {
        if (isVisible) {
          this.resetForm();
        }
        this.slideOpen.set(isVisible);
      });
    });

    effect(() => {
      const control = this.form.controls.zoneId;
      if (this.showZoneSelector()) {
        control.setValidators(Validators.required);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  protected selectZone(zoneId: number): void {
    this.form.controls.zoneId.setValue(zoneId);
    this.form.controls.zoneId.markAsDirty();
    this.selectedZoneId.set(zoneId);
  }

  protected selectArea(config: AreaConfigurationDTO): void {
    this.form.controls.areaConfigId.setValue(config.id);
    this.form.controls.name.setValue(config.name);
    this.form.controls.name.markAsDirty();
    document.getElementById("add-area-name-input")?.scrollIntoView({ behavior: "smooth" });
  }

  protected onSheetOpenChange(isOpen: boolean): void {
    if (!isOpen && this.visible()) {
      this.onCancel();
    }
  }

  protected onCancel(): void {
    if (this.isSaving()) {
      return;
    }
    this.visible.set(false);
  }

  protected onAdd(): void {
    this.form.markAllAsTouched();
    this.form.controls.name.markAsDirty();
    this.form.controls.name.updateValueAndValidity();

    if (!this.form.valid) {
      return;
    }

    const value = this.form.getRawValue();
    this.addArea.emit({
      areaConfigId: value.areaConfigId!,
      name: value.name.trim(),
      zoneId: value.zoneId ?? undefined
    });
  }

  protected nameErrorText(): string {
    const control = this.form.controls.name;

    if (control.hasError("required") || control.hasError("whitespace")) {
      return "Room name is required.";
    }

    if (control.hasError("pattern")) {
      return "Room name contains invalid characters.";
    }

    if (control.hasError("reservedRoomName")) {
      return "That room name is reserved. Please choose another.";
    }

    if (control.hasError("duplicateRoomNames")) {
      return "Room names must be unique.";
    }

    return "";
  }

  private resetForm(): void {
    this.form.reset({ zoneId: null, areaConfigId: null, name: "" });
    this.selectedZoneId.set(null);
  }

  private noWhiteSpaceValidator(control: AbstractControl): ValidationErrors | null {
    const isWhitespace = (control.value || "").trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  /** "Add New Room" is the picker's action entry, so no real room may take that name. */
  private reservedRoomNameValidator(control: AbstractControl): ValidationErrors | null {
    const name = (control.value || "").trim()
      .toLowerCase();

    return name === ADD_NEW_ROOM_AREA.name.toLowerCase() ? { reservedRoomName: true } : null;
  }

  private uniqueRoomNamesValidator(control: AbstractControl): ValidationErrors | null {
    const name = (control.value || "").trim();

    return this.existingAreas().some(area => area.name === name) ? { duplicateRoomNames: true } : null;
  }
}
